// Account-wide live call state.
//
// Unlike `useProjectCall`, this hook is not scoped to one project: it watches
// every call the signed-in user participates in, so the ring surface can be
// mounted once for the whole app and appear on any page.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { isLive, isRingExpired, stripProviderFields, type CallRow } from "@/lib/calls.core";

const LIVE = ["provisioning", "ringing", "accepted", "connecting", "active"] as const;

export function useMyCall() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [call, setCall] = useState<CallRow | null>(null);
  const [tick, setTick] = useState(0);
  const callRef = useRef<CallRow | null>(null);

  const apply = useCallback((row: Record<string, unknown> | null) => {
    const next = row ? (stripProviderFields(row) as unknown as CallRow) : null;
    // A terminal row only clears state when it belongs to the call we hold.
    if (next && !isLive(next.status)) {
      const current = callRef.current;
      if (current && current.id !== next.id) return;
      callRef.current = null;
      setCall(null);
      return;
    }
    callRef.current = next;
    setCall(next);
  }, []);

  // Any still-live call for this user, whichever project it belongs to.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from("project_calls")
        .select("*")
        .or(`initiator_user_id.eq.${userId},callee_user_id.eq.${userId}`)
        .in("status", LIVE)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) apply((data as Record<string, unknown> | null) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, apply]);

  // Realtime for every call row RLS lets this user see.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`my-calls:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "project_calls" },
        (payload) => {
          const row = payload.new as Record<string, unknown> | null;
          if (!row) return;
          if (row["initiator_user_id"] !== userId && row["callee_user_id"] !== userId) return;
          apply(row);
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, apply]);

  // Local countdown so the ring surface closes itself on expiry.
  useEffect(() => {
    if (!call || !isLive(call.status)) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [call]);

  const state = useMemo(() => {
    if (!call || !userId) {
      return { call: null, role: null as null | "caller" | "callee", ringExpired: false };
    }
    const role: "caller" | "callee" = call.initiator_user_id === userId ? "caller" : "callee";
    const ringExpired = call.status === "ringing" && isRingExpired(call.ring_expires_at);
    return { call, role, ringExpired };
    // `tick` intentionally re-derives ringExpired each second.
     
  }, [call, userId, tick]);

  return { ...state, setCall: apply, currentUserId: userId };
}
